import EscPosEncoder from '@/lib/print/EscPosEncoder';
import { createQrImage } from '@/utils';
import type { QrRenderMode } from '@/lib/print/printer-settings';

/**
 * When qrMode=auto, native QR is used for values up to this UTF-8 byte
 * length; longer values fall back to image-based QR (many firmwares fail
 * around 124–255 bytes).
 */
export const QR_AUTO_NATIVE_MAX_BYTES = 120;

export interface IRenderQrOptions {
  qrMode: QrRenderMode;
  paperWidthMm: 58 | 80;
}

export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Renders a QR into the document using the printer's configured mode:
 * - native: ESC/POS native QR command (model 2, size 6 — the historical
 *   footer-QR bytes)
 * - image: rasterized QR (most compatible for long values). Error level 'L'
 *   and 352px at 80mm keep the AFIP invoice QR byte-identical to the
 *   pre-existing hardcoded path; 58mm paper renders 256px.
 * - auto: native up to QR_AUTO_NATIVE_MAX_BYTES, image beyond.
 */
export async function renderQr(
  encoder: EscPosEncoder,
  value: string,
  options: IRenderQrOptions
): Promise<void> {
  const text = String(value ?? '').trim();
  if (!text) return;

  const mode: QrRenderMode =
    options.qrMode === 'native' || options.qrMode === 'image'
      ? options.qrMode
      : 'auto';
  const wantsNative =
    mode === 'native' ||
    (mode === 'auto' && utf8ByteLength(text) <= QR_AUTO_NATIVE_MAX_BYTES);

  if (!wantsNative) {
    try {
      const sizePx = options.paperWidthMm === 58 ? 256 : 352;
      const image = await createQrImage(text, sizePx, 'L');
      encoder.image(image, sizePx, sizePx);
      return;
    } catch (e) {
      // Image rendering unavailable (e.g. no DOM) — native QR still prints.
      console.error('[renderQr] image QR failed, falling back to native', e);
    }
  }

  encoder.qrcode(text, undefined, 6, undefined);
}
